import { useState, useEffect } from 'react';
import { SearchSelect, Text } from './FieldComponents';
import { X, Sparkles, Loader2, Trash2 } from 'lucide-react';
import { isManagedPublicFormReport } from '../utils/publicFormReports';

export const ReportList = ({ setSelectedReport, selectedReport, reports, onAIReportGenerate, onDeleteReport, variant = 'builder', headerActions = null, onSelectReport, onCreateListReport }) => {
    const [filterQuery, setFilterQuery] = useState('');
    const [aiPrompt, setAiPrompt] = useState('');
    const [isGeneratingAI, setIsGeneratingAI] = useState(false);
    const isHub = variant === 'hub';

    const selectReport = (report) => {
        if (onSelectReport) {
            onSelectReport(report);
            return;
        }

        setSelectedReport(report);
    };

    const handleAIGenerate = async () => {
        if (!aiPrompt.trim()) return;
        
        setIsGeneratingAI(true);
        try {
            await onAIReportGenerate(aiPrompt.trim());
            setAiPrompt(''); // Clear on success
        } catch (error) {
            console.error('AI generation failed:', error);
            // You might want to show an error message to the user
        } finally {
            setIsGeneratingAI(false);
        }
    };

    const matchesFilter = (report) => report.name.toLowerCase().includes(filterQuery.toLowerCase());
    const filteredCustomReports = reports.filter(
        (report) =>
            !Number(report.premade) &&
            !isManagedPublicFormReport(report) &&
            matchesFilter(report)
    );
    const publicFormReports = isHub
        ? reports.filter((report) => isManagedPublicFormReport(report) && matchesFilter(report))
        : [];

    const premadeReports = reports.filter((report) => Number(report.premade) === 1);

    return (
        <div className='report-container'>
            {/* AI Report Generation Section 
            <div className='report-list ai-reports'>
                <div className='report-list-header'>
                    Generate Report with AI
                    <Sparkles size={18} style={{ marginRight: '8px' }} />
                </div>
                <div className='ai-prompt-container'>
                    <textarea
                        className='ai-prompt-textarea'
                        placeholder='Describe the report you want to create in plain English. For example: "Show me all cases from 2024 where the claim was denied and the property damage is over $50,000, include case name, contact, and property damage amount"'
                        value={aiPrompt}
                        onChange={(e) => setAiPrompt(e.target.value)}
                        rows={4}
                        disabled={isGeneratingAI}
                    />
                    <button 
                        className='ai-generate-btn'
                        onClick={handleAIGenerate}
                        disabled={!aiPrompt.trim() || isGeneratingAI}
                    >
                        {isGeneratingAI ? (
                            <>
                                <Loader2 size={16} className="spin" style={{ marginRight: '8px' }} />
                                Generating...
                            </>
                        ) : (
                            <>
                                <Sparkles size={16} style={{ marginRight: '8px' }} />
                                Generate Report
                            </>
                        )}
                    </button>
                </div>
            </div>*/}

            <div className={`report-list custom-reports ${isHub ? 'report-hub-list-panel' : ''}`}>
                <div className='report-list-header'>
                    <span>{isHub ? 'Reports' : 'My Reports'}</span>
                    {headerActions}
                </div>
                <div className='form-group upper report-filter alt'>
                    <input
                        type='text'
                        className='form-control'
                        placeholder='Report Name'
                        onChange={(e) => setFilterQuery(e.target.value)}
                        value={filterQuery}
                    />
                    <div className='clear-filter' title='Clear report search filter'>
                        <X onClick={() => setFilterQuery('')} size={18} />
                    </div>
                </div>
                <div className='custom-report-list'>
                    {filteredCustomReports.length > 0 ? (
                        filteredCustomReports.map((report) => (
                            <div
                                key={report.id}
                                className={`report-list-name ${String(selectedReport?.id) === String(report.id) ? 'active' : ''}`}
                                onClick={() => selectReport(report)}
                            >
                                <span className='report-list-name-text'>
                                    {report.name} <span className='subtext'>({report.creator})</span>
                                </span>
                                <button
                                    type='button'
                                    className='report-list-row-action report-list-delete'
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onDeleteReport && onDeleteReport(report);
                                    }}
                                    aria-label={`Delete ${report.name}`}
                                    title='Delete report'
                                >
                                    <Trash2 size={15} />
                                </button>
                            </div>
                        ))
                    ) : publicFormReports.length === 0 ? (
                        <div className='no-results subtext'>No reports found.</div>
                    ) : null}
                    {publicFormReports.length > 0 && (
                        <section className='public-form-report-section' aria-labelledby='public-form-report-heading'>
                            <div className='public-form-report-section-heading' id='public-form-report-heading'>Public Form Reports</div>
                            {publicFormReports.map((report) => {
                                const submissionCount = Number(report.submission_count || 0);
                                return (
                                    <div
                                        key={report.id || report.report_key}
                                        className={`report-list-name public-form-report-row ${String(selectedReport?.id) === String(report.id) ? 'active' : ''}`}
                                        onClick={() => selectReport(report)}
                                    >
                                        <span className='report-list-name-text'>
                                            {report.name}
                                            <span className='public-form-report-badge'>Form</span>
                                        </span>
                                        <span className='public-form-report-count'>{submissionCount} {submissionCount === 1 ? 'submission' : 'submissions'}</span>
                                    </div>
                                );
                            })}
                        </section>
                    )}
                </div>
            </div>

            {!isHub && <div className='report-list premade-reports'>
                <div className='report-list-header'>Create New Report</div>
                {premadeReports.length > 0 ? (
                    <>
                    {premadeReports.map((report) => (
                        <div
                            key={report.id}
                            className='report-list-name'
                            onClick={() => selectReport(report)}
                        >
                            {report.name}
                        </div>
                    ))}
                    {onCreateListReport && (
                        <div
                            className='report-list-name new-list-report-option'
                            onClick={onCreateListReport}
                            role='button'
                            tabIndex={0}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    onCreateListReport();
                                }
                            }}
                        >
                            New List Report
                        </div>
                    )}
                    </>
                ) : (
                    <>
                        <div className='no-results'>No premade reports available.</div>
                        {onCreateListReport && (
                            <div
                                className='report-list-name new-list-report-option'
                                onClick={onCreateListReport}
                                role='button'
                                tabIndex={0}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        onCreateListReport();
                                    }
                                }}
                            >
                                New List Report
                            </div>
                        )}
                    </>
                )}
            </div>}
        </div>
    );
};
